// src/components/WinRateCalculator/InputControls.jsx
import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Select, SelectTrigger, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import api from "@/api/api";  // Use authenticated API instance
import { useProfitProfiles } from "../../hooks/useProfitProfiles";

export default function InputControls({ inputs, onChange }) {
  const { growthGoal, riskPerTrade, numTrades, profile, splits } = inputs;
  const { create } = useProfitProfiles();

  // Fetch user accounts for linking profiles
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);

  useEffect(() => {
    api
      .get("/accounts/")  // Adjust to your backend accounts endpoint
      .then((res) => {
        console.log("Fetched accounts:", res.data);
        setAccounts(res.data);
        // Default-select all accounts so user can deselect if needed
        setSelectedAccounts(res.data.map((a) => a.id));
      })
      .catch((err) => console.error("Failed to fetch accounts:", err));
  }, []);

  const update = (field, value) => onChange({ ...inputs, [field]: value });

  const onToggleAccount = (id) => {
    setSelectedAccounts((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const onSaveProfile = async () => {
    const name = window.prompt("Name this profit profile:", "Custom 3-step");
    if (!name) return;
  
    const { w1, w2, w3, R1, R2, R3 } = splits;
    const partial_targets = [
      { r_multiple: R1, size_pct: w1 },
      { r_multiple: R2, size_pct: w2 },
      { r_multiple: R3, size_pct: w3 },
    ];
  
    try {
      // Just one call – creates a profile under the current user
      const newProfile = await create({
        name,
        partial_targets,
        is_default: false,
      });
      alert(`✅ Saved profile “${newProfile.name}”!`);
    } catch (err) {
      console.error("Save profile error:", err);
      alert("❌ Failed to save profile.");
    }
  };
  

  return (
    <div className="space-y-4">
      {/* Growth Goal */}
      <div>
        <label className="block text-sm font-medium mb-1 text-textPrimary">Growth Goal (%)</label> {/* Label color */}
        <Slider
          value={[growthGoal]}
          max={100}
          step={0.1}
          onValueChange={([v]) => update("growthGoal", v)}
        />
        <Input
          className="mt-2"
          type="number"
          value={growthGoal}
          onChange={(e) => update("growthGoal", parseFloat(e.target.value))}
        />
      </div>

      {/* Risk per Trade */}
      <div>
        <label className="block text-sm font-medium mb-1 text-textPrimary">Risk per Trade (%)</label> {/* Label color */}
        <Slider
          value={[riskPerTrade]}
          max={5}
          step={0.1}
          onValueChange={([v]) => update("riskPerTrade", v)}
        />
        <Input
          className="mt-2"
          type="number"
          value={riskPerTrade}
          onChange={(e) => update("riskPerTrade", parseFloat(e.target.value))}
        />
      </div>

      {/* Number of Trades */}
      <div>
        <label className="block text-sm font-medium mb-1 text-textPrimary">Number of Trades</label> {/* Label color */}
        <Input
          type="number"
          min={1}
          value={numTrades}
          onChange={(e) => update("numTrades", parseInt(e.target.value, 10))}
        />
      </div>

      {/* w, r, q fields, validation, and save profile are now always visible */}
      <div className="space-y-6 pt-4"> {/* Added pt-4 for some separation */}
          {/* w & R fields */}
          <div className="grid grid-cols-3 gap-4">
            {['w1','w2','w3','R1','R2','R3'].map((key) => (
              <div key={key}>
                <label className="block text-sm font-medium mb-1 text-textPrimary">{key.toUpperCase()}</label> {/* Label color */}
                <Input
                  type="number"
                  min={0}
                  value={splits?.[key] ?? ''}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    update('splits', { ...splits, [key]: val });
                  }}
                />
              </div>
            ))}
          </div>

          {/* q fields */}
          {/* Removed max-w-md from the q fields container to allow it to be wider */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4"> {/* Changed to sm:grid-cols-3 for q fields */}
            {['q1','q2','q3'].map((key) => (
              <div key={key}>
                <label className="block text-sm font-medium mb-1 text-textPrimary">{key.toUpperCase()} (%)</label> {/* Label color */}
                <Input
                  className="w-full"
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={splits?.[key] ?? ''}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    update('splits', { ...splits, [key]: val });
                  }}
                />
              </div>
            ))}
          </div>

          {/* Validation */}
          {splits && (splits.w1 + splits.w2 + splits.w3 !== 100) && (
            <p className="text-destructive text-sm">w₁ + w₂ + w₃ must sum to 100.</p> /* Validation text color */
          )}
          {splits && (splits.q1 + splits.q2 + splits.q3 !== 100) && (
            <p className="text-destructive text-sm">q₁ + q₂ + q₃ must sum to 100%.</p> /* Validation text color */
          )}


          {/* Save Profile */}
          <div className="flex justify-end pt-4">
            <Button onClick={onSaveProfile}>Save Profile</Button>
          </div>
        </div>
      {/* End of always visible section */}
    </div>
  );
}
